import { getBossPassage } from "../data/BOSS_PASSAGES";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { db } from "../firebase/config";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { getWordsForGrade } from "../data/SIGHT_WORDS";
import SidekickBanner from "../components/SidekickBanner";
import Header from "../components/Header";
import knightSprite from "../assets/knight.png";
import princessSprite from "../assets/princess.png";
import sprocketSprite from "../assets/sprocket.png";
import muphySprite from "../assets/muphy.png";
import ruffusSprite from "../assets/ruffus.png";
import meowthaSpriteImg from "../assets/meowtha.png";
import mrSprite from "../assets/mr.png";
import mrsSprite from "../assets/mrs.png";

const SPRITE_MAP = {
  "/assets/knight.png":   knightSprite,
  "/assets/princess.png": princessSprite,
  "/assets/sprocket.png": sprocketSprite,
  "/assets/muphy.png":    muphySprite,
  "/assets/ruffus.png":   ruffusSprite,
  "/assets/meowtha.png":  meowthaSpriteImg,
  "/assets/mr.png":       mrSprite,
  "/assets/mrs.png":      mrsSprite,
};

function calcLetterGrade(pct) {
  if (pct >= 97) return "A+";
  if (pct >= 93) return "A";
  if (pct >= 90) return "A-";
  if (pct >= 87) return "B+";
  if (pct >= 83) return "B";
  if (pct >= 80) return "B-";
  if (pct >= 77) return "C+";
  if (pct >= 73) return "C";
  if (pct >= 70) return "C-";
  if (pct >= 67) return "D+";
  if (pct >= 63) return "D";
  if (pct >= 60) return "D-";
  return "F";
}

function buildSessionWords(allWords, progress, sessionSize) {
  const struggling = [];
  const learning = [];
  const mastered = [];
  const unseen = [];
  allWords.forEach((word) => {
    const p = progress[word];
    if (!p) {
      unseen.push(word);
    } else {
      const score = (p.correct || 0) - (p.missed || 0);
      if (score >= 3) mastered.push(word);
      else if (score > 0) learning.push(word);
      else struggling.push(word);
    }
  });
  function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
  const s = shuffle(struggling);
  const l = shuffle(learning);
  const u = shuffle(unseen);
  const m = shuffle(mastered);
  const struggleCount = Math.min(Math.floor(sessionSize * 0.5), s.length);
  const learnCount = Math.min(Math.floor(sessionSize * 0.3), l.length + u.length);
  const masterCount = Math.min(Math.floor(sessionSize * 0.1), m.length);
  const selected = [
    ...s.slice(0, struggleCount),
    ...[...l, ...u].slice(0, learnCount),
    ...m.slice(0, masterCount),
  ];
  const remaining = sessionSize - selected.length;
  const used = new Set(selected);
  const filler = [...u, ...s, ...l, ...m]
    .filter((w) => !used.has(w))
    .slice(0, remaining);
  return shuffle([...selected, ...filler]).slice(0, sessionSize);
}

let warmupInterval = null;
function startWarmup() {
  if (warmupInterval) return;
  warmupInterval = setInterval(() => {
    if (window.responsiveVoice) {
      window.responsiveVoice.speak(" ", "US English Female", { volume: 0 });
    }
  }, 4000);
}
function stopWarmup() {
  if (warmupInterval) {
    clearInterval(warmupInterval);
    warmupInterval = null;
  }
}

function pronounceWord(word) {
  if (window.responsiveVoice) {
    window.responsiveVoice.cancel();
    setTimeout(() => {
      window.responsiveVoice.speak(word, "US English Female", {
        rate: 0.9,
        pitch: 1,
        volume: 1,
      });
    }, 150);
    return;
  }
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.rate = 0.7;
      utterance.pitch = 1;
      utterance.volume = 1;
      window.speechSynthesis.speak(utterance);
    }, 250);
  }
}

function BossResultCard({ bossResult, bossScene, bossStars, wordPoints, totalPossible, theme, onContinue }) {
  const resultImg = bossScene
    ? require(`../assets/battleScenes/scene-boss-mr-${bossScene}-${bossResult}.webp`)
    : null;
  const totalPoints = wordPoints + bossStars;
  const combinedPct = Math.round((totalPoints / totalPossible) * 100);
  const letterGrade = calcLetterGrade(combinedPct);
  return (
    <div style={{
      borderRadius: 16, overflow: "hidden", marginBottom: 16,
      backgroundColor: "rgba(0,0,0,0.75)", border: "2px solid rgba(255,255,255,0.15)",
    }}>
      {resultImg && (
        <img src={resultImg} alt={bossResult} style={{ width: "100%", height: "auto", display: "block" }} />
      )}
      <div style={{ padding: "20px 24px", textAlign: "center" }}>
        <h3 style={{ fontSize: 24, fontWeight: 900, color: "white", fontFamily: theme.fontDisplay, margin: "0 0 8px 0", lineHeight: 1.4 }}>
          {bossResult === "victory" ? "Victory! 🏆" : "Run Away! 🏃"}
        </h3>
        <p style={{ fontSize: 15, color: "white", fontFamily: theme.fontBody, margin: "0 0 4px 0", opacity: 0.9 }}>
          {totalPoints} / {totalPossible} points — {combinedPct}% — {letterGrade}
        </p>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontFamily: theme.fontBody, margin: "0 0 20px 0" }}>
          {wordPoints} word {wordPoints === 1 ? "point" : "points"} + {bossStars} reading {bossStars === 1 ? "point" : "points"}
        </p>
        <button onClick={onContinue} style={{ width: "100%", padding: 16, backgroundColor: theme.colorPrimary, color: "white", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: theme.fontBody }}>
          {bossResult === "victory" ? "Continue Quest ➜" : "Back to Words ➜"}
        </button>
      </div>
    </div>
  );
}

export default function SightWordQuiz() {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const [bossScene, setBossScene] = useState(null);
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sessionResults, setSessionResults] = useState({});
  const [phase, setPhase] = useState("loading");
  const [wikiSummary, setWikiSummary] = useState(null);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [bossExcerpt, setBossExcerpt] = useState(null);
  const [bossLoading, setBossLoading] = useState(false);
  const [bossStars, setBossStars] = useState(0);
  const [bossResult, setBossResult] = useState(null);
  const [showNextButton, setShowNextButton] = useState(false);

  useEffect(() => {
    startWarmup();
    return () => stopWarmup();
  }, []);

  const WIKI_GRADES = ["6th","7th","8th","9th","10th","11th","12th"];
  const FULL_ARTICLE_GRADES = ["9th","10th","11th","12th"];
  const grade = userProfile?.grade || "K";
  const showWiki = WIKI_GRADES.includes(grade);
  const showFullArticle = FULL_ARTICLE_GRADES.includes(grade);

  async function fetchWiktionaryDefinition(word) {
    if (!showWiki) return;
    setWikiSummary(null);
    setWikiLoading(true);
    try {
      const res = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`
      );
      if (res.ok) {
        const data = await res.json();
        const entry = data?.[0];
        if (entry) {
          const firstMeaning = entry.meanings?.[0];
          const partOfSpeech = firstMeaning?.partOfSpeech || "";
          const definition = firstMeaning?.definitions?.[0]?.definition || "";
          const example = firstMeaning?.definitions?.[0]?.example || null;
          const additionalMeanings = entry.meanings?.slice(1, 3).map((m) => ({
            partOfSpeech: m.partOfSpeech,
            definition: m.definitions?.[0]?.definition || "",
          })) || [];
          if (definition) {
            setWikiSummary({
              title: word,
              partOfSpeech,
              definition,
              example,
              additionalMeanings,
              url: `https://en.wiktionary.org/wiki/${encodeURIComponent(word)}`,
            });
          }
        }
      }
    } catch {
      // silently fail
    }
    setWikiLoading(false);
  }

  useEffect(() => {
    async function setup() {
      if (!currentUser || !userProfile) return;
      const grade = userProfile.grade || "K";
      const sessionSize = userProfile.quizSessionSize || 10;
      const allWords = getWordsForGrade(grade);
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      const progress = snap.data()?.sightWordProgress || {};
      const sessionWords = buildSessionWords(allWords, progress, sessionSize);
      setWords(sessionWords);
      setPhase("quiz");
    }
    setup();
  }, [currentUser]);

  useEffect(() => {
    if (phase !== "quiz" || words.length === 0) return;
    fetchWiktionaryDefinition(words[currentIndex]);
  }, [currentIndex, phase, words]);

  function handleKnowIt() {
    const word = words[currentIndex];
    setSessionResults((prev) => ({ ...prev, [word]: "correct" }));
    advance();
  }

  function handleSayIt() {
    const word = words[currentIndex];
    pronounceWord(word);
    setSessionResults((prev) => ({ ...prev, [word]: "missed" }));
    setShowNextButton(true);
  }

  async function loadBossExcerpt() {
    setBossLoading(true);
    setBossStars(0);
    setBossResult(null);
    const randomMonster = Math.floor(Math.random() * 10) + 1;
    setBossScene(randomMonster);
    const snap = await getDoc(doc(db, "users", currentUser.uid));
    const position = snap.data()?.readAloudProgress?.currentPosition || 0;
    const result = getBossPassage(grade, position);
    setBossExcerpt({
      excerpt: result.text,
      bookTitle: result.bookTitle,
      newPosition: result.newPosition,
    });
    setBossLoading(false);
    setPhase("cutscene");
  }

  async function handleBossRating(stars) {
    setBossStars(stars);
    const wordPoints = correctWords.length;
    const totalPoints = wordPoints + stars;
    const totalPossible = words.length + 5;
    const combinedPct = Math.round((totalPoints / totalPossible) * 100);
    const victory = combinedPct >= 80;
    setBossResult(victory ? "victory" : "defeat");
    try {
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      const existing = snap.data()?.readAloudProgress || {};
      const sessions = existing.sessions || [];
      await updateDoc(doc(db, "users", currentUser.uid), {
        readAloudProgress: {
          currentPosition: victory
            ? (bossExcerpt?.newPosition || 0)
            : (existing.currentPosition || 0),
          sessions: [
            ...sessions,
            {
              date: new Date().toISOString().split("T")[0],
              passage: bossExcerpt?.excerpt?.slice(0, 100) + "...",
              stars,
              wordPoints,
              totalPoints,
              combinedPct,
              victory,
            },
          ],
        },
      });
    } catch (err) {
      console.error("Failed to save boss fight result:", err);
    }
  }

  function continuAfterBoss() {
    setBossExcerpt(null);
    setPhase("celebration");
  }

  const saveResults = useCallback(async function(results) {
    try {
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      const existing = snap.data()?.sightWordProgress || {};
      const updated = { ...existing };
      Object.entries(results).forEach(([word, result]) => {
        const prev = updated[word] || { correct: 0, missed: 0 };
        updated[word] = {
          correct: prev.correct + (result === "correct" ? 1 : 0),
          missed: prev.missed + (result === "missed" ? 1 : 0),
          lastSeen: new Date().toISOString().split("T")[0],
        };
      });
      await updateDoc(doc(db, "users", currentUser.uid), {
        sightWordProgress: updated,
      });
    } catch (err) {
      console.error("Failed to save sight word results:", err);
    }
  }, [currentUser]);

  const advance = useCallback(function() {
    setWikiSummary(null);
    setShowNextButton(false);
    const nextIndex = currentIndex + 1;
    if (nextIndex >= words.length) {
      setSessionResults((latest) => {
        saveResults(latest);
        return latest;
      });
      setPhase("boss");
      loadBossExcerpt();
    } else {
      setCurrentIndex(nextIndex);
    }
  }, [currentIndex, words.length, saveResults, loadBossExcerpt]);

  const correctWords = Object.entries(sessionResults)
    .filter(([, r]) => r === "correct")
    .map(([w]) => w);

  const missedWords = Object.entries(sessionResults)
    .filter(([, r]) => r === "missed")
    .map(([w]) => w);

  // ── Render: loading ────────────────────────
  if (phase === "loading") {
    return (
      <div style={styles.container(theme)}>
        <Header />
        <div style={styles.centered}>
          <p style={styles.loadingText(theme)}>Loading your words...</p>
        </div>
      </div>
    );
  }

  // ── Render: quiz ───────────────────────────
  if (phase === "quiz") {
    const word = words[currentIndex];
    const progress = Math.round((currentIndex / words.length) * 100);
    return (
      <div style={styles.container(theme)}>
        <Header />
        <div style={styles.content}>
          <SidekickBanner />
          <div style={styles.progressBar(theme)}>
            <div style={{ ...styles.progressFill(theme), width: `${progress}%` }} />
          </div>
          <p style={styles.progressText(theme)}>
            Word {currentIndex + 1} of {words.length}
          </p>
          <div style={styles.card(theme)}>
            <p style={styles.wordLabel(theme)}>Can you read this word?</p>
            <div style={styles.wordDisplay(theme)}>
              <p style={styles.word(theme)}>{word}</p>
            </div>
            <div style={styles.buttonRow}>
              {showNextButton ? (
                <button style={styles.nextWordBtn(theme)} onClick={advance}>
                  Next Word ➜
                </button>
              ) : (
                <button style={styles.knowItBtn(theme)} onClick={handleKnowIt}>
                  ✅ I know it!
                </button>
              )}
              <button
                style={showNextButton
                  ? { ...styles.sayItBtn(theme), opacity: 0.4, cursor: "not-allowed" }
                  : styles.sayItBtn(theme)}
                onClick={handleSayIt}
                disabled={showNextButton}
              >
                🔊 Say it for me
              </button>
            </div>
          </div>
          {wikiLoading && (
            <div style={styles.wikiCard(theme)}>
              <p style={styles.wikiLoading(theme)}>📖 Looking up "{words[currentIndex]}"...</p>
            </div>
          )}
          {wikiSummary && !wikiLoading && (
            <div style={styles.wikiCard(theme)}>
              <p style={styles.wikiLabel(theme)}>📖 Definition</p>
              <div style={styles.wikiHeaderRow}>
                <h3 style={styles.wikiTitle(theme)}>{wikiSummary.title}</h3>
                {wikiSummary.partOfSpeech && (
                  <span style={styles.partOfSpeech(theme)}>{wikiSummary.partOfSpeech}</span>
                )}
              </div>
              <p style={styles.wikiText(theme)}>{wikiSummary.definition}</p>
              {wikiSummary.example && (
                <p style={styles.wikiExample(theme)}>"{wikiSummary.example}"</p>
              )}
              {wikiSummary.additionalMeanings?.length > 0 && (
                <div style={styles.additionalMeanings}>
                  {wikiSummary.additionalMeanings.map((m, i) => (
                    <div key={i} style={styles.additionalMeaning(theme)}>
                      <span style={styles.partOfSpeech(theme)}>{m.partOfSpeech}</span>
                      <p style={styles.wikiText(theme)}>{m.definition}</p>
                    </div>
                  ))}
                </div>
              )}
              {showFullArticle && wikiSummary.url && (
                <a
                  href={wikiSummary.url}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.wikiLink(theme)}
                >
                  Read full entry on Wiktionary →
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render: cutscene ───────────────────────
  if (phase === "cutscene") {
    const openingImg = bossScene
      ? require(`../assets/battleScenes/scene-boss-mr-${bossScene}-opening.webp`)
      : null;

    // Auto-advance after 6 seconds
    setTimeout(() => {
      if (phase === "cutscene") setPhase("boss");
    }, 6000);

    return (
      <div
        onClick={() => setPhase("boss")}
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          cursor: "pointer",
          backgroundColor: "#000",
          zIndex: 999,
        }}
      >
        {openingImg && (
          <img
            src={openingImg}
            alt="Boss cutscene"
            style={{
              // Height fills the full viewport — width overflows naturally
              // since the image is landscape and the screen is portrait
              position: "absolute",
              top: 0,
              left: "50%",
              height: "100%",
              width: "auto",
              // Ken Burns: pan left-to-right across the wide image
              // with a gentle zoom pulse
              animation: "kenBurnsPan 6s ease-in-out forwards, rumble 0.15s ease-in-out infinite",
              transformOrigin: "center center",
            }}
          />
        )}

        {/* Tap to skip label */}
        <div style={{
          position: "absolute",
          bottom: 40,
          left: 0,
          right: 0,
          textAlign: "center",
          color: "rgba(255,255,255,0.7)",
          fontSize: 13,
          fontFamily: theme.fontBody,
          letterSpacing: 1,
          zIndex: 1,
        }}>
          Tap to skip
        </div>

        <style>{`
          @keyframes kenBurnsPan {
            0%   { transform: translateX(-50%) translateX(0%)   scale(1.0); }
            15%  { transform: translateX(-50%) translateX(-5%)  scale(1.05); }
            30%  { transform: translateX(-50%) translateX(-12%) scale(1.1); }
            45%  { transform: translateX(-50%) translateX(-18%) scale(1.08); }
            60%  { transform: translateX(-50%) translateX(-12%) scale(1.12); }
            75%  { transform: translateX(-50%) translateX(-6%)  scale(1.06); }
            90%  { transform: translateX(-50%) translateX(-15%) scale(1.1); }
            100% { transform: translateX(-50%) translateX(-8%)  scale(1.0); }
          }
          @keyframes rumble {
            0%   { margin-top: 0px;  margin-left: 0px; }
            20%  { margin-top: -3px; margin-left: 2px; }
            40%  { margin-top: 2px;  margin-left: -3px; }
            60%  { margin-top: -2px; margin-left: -2px; }
            80%  { margin-top: 3px;  margin-left: 1px; }
            100% { margin-top: 0px;  margin-left: 0px; }
          }
        `}</style>
      </div>
    );
  }

  // ── Render: boss ───────────────────────────
  if (phase === "boss") {
    const openingImg = bossScene
      ? require(`../assets/battleScenes/scene-boss-mr-${bossScene}-opening.webp`)
      : null;
    const resultImg = bossResult && bossScene
      ? require(`../assets/battleScenes/scene-boss-mr-${bossScene}-${bossResult}.webp`)
      : null;
    const bgImg = resultImg || openingImg;
    return (
      <div style={{
        minHeight: "100vh",
        fontFamily: theme.fontBody,
        ...(bgImg ? {
          backgroundImage: `url(${bgImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : { backgroundColor: theme.bgPage }),
      }}>
        <Header />
        <div style={styles.content}>
          {bossLoading && (
            <div style={styles.bossCard(theme)}>
              <p style={styles.bossLoading(theme)}>Loading your challenge...</p>
            </div>
          )}
          {bossExcerpt && !bossResult && (
            <div style={styles.bossCard(theme)}>
              <p style={styles.bossEyebrow(theme)}>⚔️ BOSS FIGHT</p>
              <h2 style={styles.bossTitle(theme)}>Reading Challenge!</h2>
              <p style={styles.bossSub(theme)}>Read this passage out loud to your parent.</p>
              <p style={styles.bossBook(theme)}>📖 From: {bossExcerpt.bookTitle}</p>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", margin: "16px 0" }} />
              {bossExcerpt.excerpt.split("\n\n").map((para, i) => (
                <p key={i} style={styles.bossPassage(theme)}>{para}</p>
              ))}
              <p style={styles.ratingLabel(theme)}>Parent: how did they do?</p>
              <div style={styles.starRow}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    style={styles.starBtn(theme, star, bossStars)}
                    onClick={() => handleBossRating(star)}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
          )}
          {bossResult && (
            <BossResultCard
              bossResult={bossResult}
              bossScene={bossScene}
              bossStars={bossStars}
              wordPoints={correctWords.length}
              totalPossible={words.length + 5}
              theme={theme}
              onContinue={continuAfterBoss}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Render: celebration ────────────────────
  if (phase === "celebration") {
    const wordPoints = correctWords.length;
    const totalPoints = wordPoints + bossStars;
    const totalPossible = words.length + 5;
    const combinedPct = Math.round((totalPoints / totalPossible) * 100);
    const letterGrade = calcLetterGrade(combinedPct);
    const resultImg = bossScene && bossResult
      ? require(`../assets/battleScenes/scene-boss-mr-${bossScene}-${bossResult}.webp`)
      : null;
    const gradeColor = combinedPct >= 80
      ? theme.colorSuccess
      : combinedPct >= 70
      ? theme.colorAccent
      : theme.colorPrimary;
    return (
      <div style={styles.container(theme)}>
        <Header />
        <div style={styles.centered}>
          {resultImg && (
            <img
              src={resultImg}
              alt="Result"
              style={{
                width: "100%",
                maxWidth: 400,
                borderRadius: 16,
                marginBottom: 20,
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              }}
            />
          )}
          <img
            src={SPRITE_MAP[theme.sidekickSprite] || null}
            alt={theme.sidekickName}
            style={styles.celebrationSprite}
          />
          <h2 style={styles.celebrationTitle(theme)}>
            {combinedPct >= 80 ? "Amazing job! 🎉" : "Keep it up! 💪"}
          </h2>
          <div style={{
            backgroundColor: gradeColor,
            borderRadius: 16,
            padding: "20px 32px",
            marginBottom: 20,
            textAlign: "center",
          }}>
            <p style={{
              fontSize: 48,
              fontWeight: 900,
              color: "white",
              margin: "0 0 4px 0",
              fontFamily: theme.fontDisplay,
              lineHeight: 1,
            }}>
              {letterGrade}
            </p>
            <p style={{
              fontSize: 20,
              fontWeight: 800,
              color: "white",
              margin: "0 0 4px 0",
              fontFamily: theme.fontBody,
            }}>
              {combinedPct}%
            </p>
            <p style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.85)",
              margin: 0,
              fontFamily: theme.fontBody,
            }}>
              {totalPoints} / {totalPossible} points
            </p>
          </div>
          <div style={{
            display: "flex",
            gap: 12,
            marginBottom: 24,
            justifyContent: "center",
            flexWrap: "wrap",
          }}>
            <div style={{
              backgroundColor: theme.bgCard,
              border: `2px solid ${theme.bgCardBorder}`,
              borderRadius: 12,
              padding: "12px 20px",
              textAlign: "center",
            }}>
              <p style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 900,
                color: theme.colorText,
                fontFamily: theme.fontBody,
              }}>
                {wordPoints}/{words.length}
              </p>
              <p style={{
                margin: 0,
                fontSize: 12,
                color: theme.colorTextSoft,
                fontFamily: theme.fontBody,
              }}>
                Words
              </p>
            </div>
            <div style={{
              backgroundColor: theme.bgCard,
              border: `2px solid ${theme.bgCardBorder}`,
              borderRadius: 12,
              padding: "12px 20px",
              textAlign: "center",
            }}>
              <p style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 900,
                color: theme.colorText,
                fontFamily: theme.fontBody,
              }}>
                {bossStars}/5
              </p>
              <p style={{
                margin: 0,
                fontSize: 12,
                color: theme.colorTextSoft,
                fontFamily: theme.fontBody,
              }}>
                Reading
              </p>
            </div>
          </div>
          <button
            style={styles.continueBtn(theme)}
            onClick={() => setPhase("summary")}
          >
            See Details ➜
          </button>
        </div>
      </div>
    );
  }

  // ── Render: summary ────────────────────────
  if (phase === "summary") {
    return (
      <div style={styles.container(theme)}>
        <Header />
        <div style={styles.content}>
          <h2 style={styles.summaryTitle(theme)}>Session Summary</h2>
          {correctWords.length > 0 && (
            <div style={styles.summaryCard(theme)}>
              <h3 style={styles.summaryCardTitle(theme)}>
                ✅ Words you knew ({correctWords.length})
              </h3>
              <div style={styles.wordChips}>
                {correctWords.map((w) => (
                  <span key={w} style={styles.chipCorrect(theme)}>{w}</span>
                ))}
              </div>
            </div>
          )}
          {missedWords.length > 0 && (
            <div style={styles.summaryCard(theme)}>
              <h3 style={styles.summaryCardTitle(theme)}>
                🔊 Words to practice ({missedWords.length})
              </h3>
              <div style={styles.wordChips}>
                {missedWords.map((w) => (
                  <span key={w} style={styles.chipMissed(theme)}>{w}</span>
                ))}
              </div>
            </div>
          )}
          <button
            style={styles.continueBtn(theme)}
            onClick={() => navigate("/")}
          >
            Back to My Books
          </button>
        </div>
      </div>
    );
  }

  return null;
}

const styles = {
  container: (theme) => ({
    minHeight: "100vh",
    backgroundColor: theme.bgPage,
    fontFamily: theme.fontBody,
  }),
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "70vh",
    padding: "24px",
    textAlign: "center",
  },
  content: {
    padding: "24px",
    maxWidth: 600,
    margin: "0 auto",
  },
  loadingText: (theme) => ({
    color: theme.colorText,
    fontSize: 18,
    fontFamily: theme.fontBody,
  }),
  progressBar: (theme) => ({
    height: 10,
    backgroundColor: theme.bgCard,
    borderRadius: 5,
    marginBottom: 8,
    border: `1px solid ${theme.bgCardBorder}`,
    overflow: "hidden",
  }),
  progressFill: (theme) => ({
    height: "100%",
    backgroundColor: theme.colorPrimary,
    borderRadius: 5,
    transition: "width 0.3s ease",
  }),
  progressText: (theme) => ({
    fontSize: 13,
    color: theme.colorTextSoft,
    marginBottom: 20,
    textAlign: "right",
  }),
  card: (theme) => ({
    backgroundColor: theme.bgCard,
    border: `2px solid ${theme.bgCardBorder}`,
    borderRadius: 20,
    padding: "32px 24px",
    textAlign: "center",
    marginBottom: 24,
  }),
  wordLabel: (theme) => ({
    fontSize: 14,
    color: theme.colorTextSoft,
    marginBottom: 24,
    fontFamily: theme.fontBody,
  }),
  wordDisplay: (theme) => ({
    backgroundColor: theme.bgPage,
    border: `3px solid ${theme.colorAccent}`,
    borderRadius: 16,
    padding: "32px 24px",
    marginBottom: 32,
    overflow: "hidden",
  }),
  word: (theme) => ({
    fontSize: "clamp(16px, 8vw, 64px)",
    fontWeight: 900,
    color: theme.colorText,
    margin: 0,
    fontFamily: theme.fontDisplay,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  }),
  buttonRow: {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  knowItBtn: (theme) => ({
    flex: 1,
    minWidth: 140,
    padding: "16px 20px",
    backgroundColor: theme.colorSuccess,
    color: "white",
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: theme.fontBody,
  }),
  sayItBtn: (theme) => ({
    flex: 1,
    minWidth: 140,
    padding: "16px 20px",
    backgroundColor: theme.colorPrimary,
    color: "white",
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: theme.fontBody,
  }),
  nextWordBtn: (theme) => ({
    width: "100%",
    padding: 14,
    backgroundColor: theme.colorAccent,
    color: "#3d2b00",
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: theme.fontBody,
    marginTop: 8,
    marginBottom: 16,
  }),
  celebrationSprite: {
    width: 120,
    height: 120,
    objectFit: "contain",
    imageRendering: "pixelated",
    marginBottom: 16,
  },
  celebrationTitle: (theme) => ({
    fontSize: 32,
    fontWeight: 900,
    color: theme.colorAccent,
    fontFamily: theme.fontDisplay,
    margin: "0 0 16px 0",
    lineHeight: 1.4,
  }),
  continueBtn: (theme) => ({
    width: "100%",
    maxWidth: 300,
    padding: 16,
    backgroundColor: theme.colorPrimary,
    color: "white",
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: theme.fontBody,
  }),
  summaryTitle: (theme) => ({
    fontSize: 24,
    fontWeight: 900,
    color: theme.colorText,
    fontFamily: theme.fontDisplay,
    marginBottom: 20,
    lineHeight: 1.4,
  }),
  summaryCard: (theme) => ({
    backgroundColor: theme.bgCard,
    border: `2px solid ${theme.bgCardBorder}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  }),
  summaryCardTitle: (theme) => ({
    fontSize: 16,
    fontWeight: 800,
    color: theme.colorText,
    fontFamily: theme.fontBody,
    margin: "0 0 12px 0",
  }),
  wordChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  chipCorrect: (theme) => ({
    padding: "6px 14px",
    backgroundColor: theme.colorSuccess,
    color: "white",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: theme.fontBody,
  }),
  chipMissed: (theme) => ({
    padding: "6px 14px",
    backgroundColor: theme.colorPrimary,
    color: "white",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: theme.fontBody,
  }),
  wikiCard: (theme) => ({
    backgroundColor: theme.bgCard,
    border: `2px solid ${theme.bgCardBorder}`,
    borderRadius: 16,
    padding: "20px 24px",
    marginBottom: 24,
  }),
  wikiLoading: (theme) => ({
    color: theme.colorTextSoft,
    fontSize: 14,
    fontFamily: theme.fontBody,
    margin: 0,
    textAlign: "center",
  }),
  wikiLabel: (theme) => ({
    fontSize: 12,
    fontWeight: 700,
    color: theme.colorAccent,
    fontFamily: theme.fontBody,
    margin: "0 0 6px 0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }),
  wikiTitle: (theme) => ({
    fontSize: 18,
    fontWeight: 800,
    color: theme.colorText,
    fontFamily: theme.fontBody,
    margin: "0 0 10px 0",
  }),
  wikiText: (theme) => ({
    fontSize: 15,
    color: theme.colorText,
    fontFamily: theme.fontBody,
    lineHeight: 1.7,
    margin: "0 0 16px 0",
  }),
  wikiLink: (theme) => ({
    display: "block",
    fontSize: 14,
    color: theme.colorPrimary,
    fontFamily: theme.fontBody,
    fontWeight: 700,
    marginBottom: 16,
    textDecoration: "none",
  }),
  wikiHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
    flexWrap: "wrap",
  },
  partOfSpeech: (theme) => ({
    fontSize: 11,
    fontWeight: 700,
    color: theme.colorPrimary,
    fontFamily: theme.fontBody,
    textTransform: "uppercase",
    letterSpacing: "1px",
    backgroundColor: theme.bgPage,
    padding: "2px 8px",
    borderRadius: 20,
    border: `1px solid ${theme.bgCardBorder}`,
  }),
  wikiExample: (theme) => ({
    fontSize: 14,
    color: theme.colorTextSoft,
    fontFamily: theme.fontBody,
    fontStyle: "italic",
    lineHeight: 1.6,
    margin: "0 0 12px 0",
    borderLeft: `3px solid ${theme.colorAccent}`,
    paddingLeft: 10,
  }),
  additionalMeanings: {
    marginTop: 12,
    borderTop: "1px solid rgba(0,0,0,0.1)",
    paddingTop: 12,
  },
  additionalMeaning: (theme) => ({
    marginBottom: 8,
  }),
  bossCard: (theme) => ({
    backgroundColor: "rgba(0,0,0,0.72)",
    border: "2px solid rgba(255,255,255,0.15)",
    borderRadius: 16,
    padding: "24px",
    marginBottom: 16,
  }),
  bossLoading: (theme) => ({
    color: "rgba(255,255,255,0.7)",
    fontSize: 15,
    fontFamily: theme.fontBody,
    textAlign: "center",
    margin: 0,
  }),
  bossEyebrow: (theme) => ({
    fontSize: 12,
    fontWeight: 800,
    color: theme.colorAccent,
    fontFamily: theme.fontDisplay,
    letterSpacing: "2px",
    margin: "0 0 8px 0",
  }),
  bossTitle: (theme) => ({
    fontSize: 28,
    fontWeight: 900,
    color: "#ffffff",
    fontFamily: theme.fontDisplay,
    margin: "0 0 8px 0",
    lineHeight: 1.4,
    textShadow: "0 2px 8px rgba(0,0,0,0.8)",
  }),
  bossSub: (theme) => ({
    fontSize: 15,
    color: "rgba(255,255,255,0.85)",
    fontFamily: theme.fontBody,
    margin: "0 0 6px 0",
  }),
  bossBook: (theme) => ({
    fontSize: 13,
    color: theme.colorAccent,
    fontFamily: theme.fontBody,
    fontWeight: 700,
    margin: 0,
  }),
  bossPassage: (theme) => ({
    fontSize: 17,
    color: "#ffffff",
    fontFamily: theme.fontBody,
    lineHeight: 1.8,
    margin: "0 0 16px 0",
  }),
  ratingLabel: (theme) => ({
    fontSize: 13,
    fontWeight: 700,
    color: "rgba(255,255,255,0.7)",
    fontFamily: theme.fontBody,
    textAlign: "center",
    margin: "0 0 12px 0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }),
  starRow: {
    display: "flex",
    justifyContent: "center",
    gap: 8,
  },
  starBtn: (theme, star, selected) => ({
    fontSize: 40,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: star <= selected ? theme.colorAccent : "rgba(255,255,255,0.3)",
    transition: "color 0.15s ease",
    padding: "0 4px",
  }),
  bossResultTitle: (theme) => ({
    fontSize: 24,
    fontWeight: 900,
    color: "white",
    fontFamily: theme.fontDisplay,
    margin: "0 0 8px 0",
    lineHeight: 1.4,
  }),
  bossResultSub: (theme) => ({
    fontSize: 15,
    color: "white",
    fontFamily: theme.fontBody,
    margin: "0 0 8px 0",
    opacity: 0.9,
  }),
};